import * as Sentry from "@sentry/react";
import Auth from "../../Auth";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import Character from "../Character";
import { useState } from "react";
import { useToast } from "../ToastWrapper";

export const useReorderCharacters = ({ characters, projectId, updateCharacterData }) => {
  const [draggingItem, setDraggingItem] = useState(null);
  const [draggingLock, setDraggingLock] = useState(false);
  const { setToast } = useToast();

  const reorderCharacters = async (draggingId, overId, isStart) => {
    if (draggingId === overId) {
      // short circuit no-op.  It was let up over itself.
      return;
    }

    const originalState = [...characters];

    const draggedIndex = characters.findIndex((character) => character.id === draggingId);
    const draggedCharacter = characters[draggedIndex];
    const overIndex = characters.findIndex((character) => character.id === overId);
    const overCharacter = characters[overIndex];
    const updatedCharacter = { ...draggedCharacter };
    if (overIndex === characters.length - 1) {
      updatedCharacter.displayOrder = overCharacter.displayOrder + 1;
    } else if (isStart) {
      if (draggedIndex === 0) {
        // short circuit no-op.  It was already at the start.
        return;
      }
      const minimumDisplayOrder = Math.min(...characters.map((c) => c.displayOrder));
      updatedCharacter.displayOrder = minimumDisplayOrder - 1;
    } else {
      if (overIndex === draggedIndex - 1) {
        // short circuit no-op.  It was let up over itself on the upper side.
        return;
      }
      const neighborIndex = overIndex + 1;
      const neighborCharacter = characters[neighborIndex];
      // get the space between the two display orders.
      const displayOrderSpace = Math.abs(neighborCharacter.displayOrder - overCharacter.displayOrder);
      // just choose a random number between the two characters
      const newDisplayOrder = Math.random() * displayOrderSpace + Math.min(overCharacter.displayOrder, neighborCharacter.displayOrder);
      updatedCharacter.displayOrder = newDisplayOrder;
    }
    // Optimistically update the display order
    updateCharacterData(updatedCharacter);

    try {
      // The timeout is there so that the UI doesn't flash when the drag is over
      setTimeout(() => {
        setDraggingLock(true);
      }, 200);

      const response = await Auth.fetchWithIdToken(`/api/characters/reorder/${draggingId}`, {
        method: "PUT",
        displayOrder: updatedCharacter.displayOrder,
        projectId,
      });

      if (!response.success) throw new Error("Failed to reorder characters");
    } catch (err) {
      updateCharacterData(originalState);
      Sentry.captureException(err, { tags: { projectId, charactersBeta: true } });
      setToast("Failed to reorder characters");
    } finally {
      setDraggingLock(false);
    }
  };

  // --------------------- DRAGGING FUNCTIONALITY --------------------------

  /**
   * @param event {import('@dnd-kit/core').DragStartEvent}
   * */
  const onDragStart = ({ active }) => {
    if (!active) {
      return;
    }
    setDraggingItem(active.id);
  };

  /**
   * @param event {import('@dnd-kit/core').DragEndEvent}
   * */
  const onDragEnd = ({ over }) => {
    setDraggingItem(null);
    if (over) {
      reorderCharacters(draggingItem, over.id, over.data?.current?.start);
    }
  };

  const onDragCancel = () => {
    setDraggingItem(null);
  };

  return {
    draggingItem,
    onDragStart,
    onDragEnd,
    onDragCancel,
    draggingLock,
  };
};

export const DroppableArea = ({ start, characterId }) => {
  const { attributes, setNodeRef, isOver } = useDroppable({ id: characterId, data: { start } });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      style={{
        height: "6px",
        borderRadius: "4px",
        backgroundColor: isOver ? "var(--primary-cta)" : "transparent",
        margin: "4px",
      }}
    />
  );
};

export const SortableCharacter = ({ character, projectId, isDragging, updateCharacterData, draggingLock, isSelected, handleSelect, onDeleteMultiple, isDeleting, selectedItems, syncing, getTemplateForRole, roleOptions }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef } = useDraggable({ id: character.id });

  return (
    <div
      className="entity"
      ref={setNodeRef}
      {...attributes}
      style={{
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      <Character
        draggingLock={draggingLock}
        initialCharacter={character}
        projectId={projectId}
        isSelected={isSelected}
        handleSelect={handleSelect}
        dragListeners={listeners}
        isDragging={isDragging}
        updateCharacterData={updateCharacterData}
        setActivatorNodeRef={setActivatorNodeRef}
        onDeleteMultiple={onDeleteMultiple}
        isDeleting={isDeleting}
        selectedItems={selectedItems}
        syncing={syncing}
        getTemplateForRole={getTemplateForRole}
        roleOptions={roleOptions}
      />
    </div>
  );
};
